import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import { FooterButton } from '@/components/common/FooterButton';
import WidgetHeader from '@/components/common/WidgetHeader';
import SingleDate from '@/components/date/SingleDate';
import { useBalanceSheetData } from '@/hooks/useBalanceSheetData';
import { useFetchTimeout } from '@/modules/balanceSheet/hooks/useFetchTimeout';
import BalanceSheetDetails from '@/modules/balanceSheet/balanceSheetDetails';
import BalanceSheetSkeleton from '@/modules/balanceSheet/BalanceSheetSkeleton';
import BalanceSheetStateMessage from '@/modules/balanceSheet/BalanceSheetStateMessage';
import { withoutZeroCategories } from '@/modules/balanceSheet/withoutZeroCategories';
import { ContentContainer } from '@/widgets/contentContainer';
import { useLocalization } from '@/hooks/useLocalization';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useSessionDate } from '@/hooks/useSessionDate';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { paths } from '@/constants/paths';
import { dashboardMobileWidthValue } from '@/constants';
import { DataRangeFilterEnum } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import type { ChartPercentModes } from '@/components/charts/CategoryBarChart';
import { formatUsLocaleDate, parseStrictAppDate } from '@/utils/date';
import dayjs from '@/config/dayjs';
import {
  selectExcludedAccounts,
  selectExcludedAccountTypes,
  selectExcludedCategories,
  selectExcludedInstitutions,
  selectFilterClientScope,
} from '@/store/filterSlice';
import type { WARootState } from '@/store/store';
import type { StandardWidgetProps } from '@/models/WidgetModels';

const FETCH_TIMEOUT_MS = 15_000;
const AS_OF_DATE_SESSION_KEY = 'advisor-balancesheet-grid:as-of-date';

export default function BalanceSheetWidget({
  isWidget = true,
  isHalfWidth = false,
  title,
  onDataLoaded,
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const navigate = useNavigate();
  // Dashboard widgets are filtered by dashboard visibility, not by the route
  // grant, so an advisor can hold this widget without the Balance Sheet page.
  const canOpenBalanceSheetPage = useCanNavigateTo(
    paths.advisor.clientsWealth.balanceSheet,
  );
  const { ref: widgetWidthRef, isBelow: isWidgetNarrow } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const narrowDateField = isWidget && isWidgetNarrow;
  // The half width flag says which slot the dashboard gave the widget, but the
  // measured width is what the content actually has: a full width widget can
  // still be squeezed under the threshold by the sidebar or an embeddable host.
  const stackSections = isHalfWidth || isWidgetNarrow;

  const [asOfDate, setAsOfDate] = useSessionDate(AS_OF_DATE_SESSION_KEY, () =>
    formatUsLocaleDate(new Date()),
  );
  // Held here rather than in the details, which unmount for the skeleton, the
  // error and the empty state, so a reload doesn't throw the advisor back to
  // table view with the $/% choice lost.
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Table);
  const [chartPercentModes, setChartPercentModes] = useState<ChartPercentModes>(
    { assets: true, liabilities: true },
  );
  const [retryAttempt, setRetryAttempt] = useState(0);

  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const excludedAccounts = useSelector((state: WARootState) =>
    selectExcludedAccounts(state, userId, currentRole),
  );
  const excludedCategories = useSelector((state: WARootState) =>
    selectExcludedCategories(state, userId, currentRole),
  );
  const excludedAccountTypes = useSelector((state: WARootState) =>
    selectExcludedAccountTypes(state, userId, currentRole),
  );
  const excludedInstitutions = useSelector((state: WARootState) =>
    selectExcludedInstitutions(state, userId, currentRole),
  );
  const filterClientScope = useSelector((state: WARootState) =>
    selectFilterClientScope(state, userId, currentRole),
  );

  // The dashboard's filter modal scopes every other balance sheet surface, so
  // the widget has to read the same exclusions or it shows unfiltered totals
  // behind an active filter badge.
  const accountFilters = useMemo(() => {
    // Account exclusions can name one client's accounts, and the slice carries
    // a marker saying whose. This widget reports the whole book, so it honours
    // them only while they are advisor-wide; the taxonomy exclusions mean the
    // same thing for every client and always apply.
    const bookWideExcludedAccounts =
      filterClientScope === undefined ? excludedAccounts : [];
    const hasFilters =
      bookWideExcludedAccounts.length > 0 ||
      excludedCategories.length > 0 ||
      excludedAccountTypes.length > 0 ||
      excludedInstitutions.length > 0;
    if (!hasFilters) return undefined;
    return {
      excludedAccounts: bookWideExcludedAccounts.length
        ? bookWideExcludedAccounts
        : undefined,
      excludedCategories: excludedCategories.length
        ? excludedCategories
        : undefined,
      excludedAccountTypes: excludedAccountTypes.length
        ? excludedAccountTypes
        : undefined,
      excludedInstitutions: excludedInstitutions.length
        ? excludedInstitutions
        : undefined,
    };
  }, [
    excludedAccounts,
    filterClientScope,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
  ]);

  // The calendar disables future days, but a typed date reaches onChange
  // regardless, and there are no balances to read past today.
  const handleDateChange = (date: string) => {
    const parsed = parseStrictAppDate(date);
    if (!parsed || parsed.isAfter(dayjs(), 'day')) return;
    setAsOfDate(date);
  };

  const {
    balanceData,
    isFetching,
    isError,
    refetch,
    isEmpty,
    isUninitialized,
  } = useBalanceSheetData(
    asOfDate,
    undefined,
    accountFilters,
    undefined,
    retryAttempt,
  );

  const isLoading = isFetching || isUninitialized;

  const { timedOut, restart: restartTimeout } = useFetchTimeout(
    isLoading,
    FETCH_TIMEOUT_MS,
    asOfDate,
  );

  // Hosts that count widgets in, such as the report provider, wait on this to
  // decide everything has finished. A timeout or an error is finished too,
  // otherwise a widget that never resolves holds the count open forever.
  const hasSettled = !isLoading || timedOut;
  // WidgetLoader rebuilds its callback on every render and the callback moves
  // host state, so firing on identity alone would report the same request over
  // and over and drive the host round again. Report each request once.
  const reportedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!hasSettled || reportedFor.current === asOfDate) return;
    reportedFor.current = asOfDate;
    onDataLoaded?.();
  }, [hasSettled, asOfDate, onDataLoaded]);

  // A refetch while the timed-out request still hangs only re-attaches to it,
  // so a retry after a timeout asks for a fresh request instead.
  const handleRetry = () => {
    restartTimeout();
    if (isUninitialized) return;
    if (timedOut) {
      setRetryAttempt((attempt) => attempt + 1);
      return;
    }
    refetch();
  };

  const displayBalanceData = useMemo(
    () => withoutZeroCategories(balanceData),
    [balanceData],
  );

  const goToBalanceSheetPage = () => {
    const query = new URLSearchParams({ asOfDate });
    void navigate(
      `${paths.advisor.clientsWealth.balanceSheet}?${query.toString()}`,
    );
  };

  const hasNoCategories =
    displayBalanceData.assets.categories.length === 0 &&
    displayBalanceData.liabilities.categories.length === 0;

  const renderBody = () => {
    if (isLoading && !timedOut) {
      return <BalanceSheetSkeleton stacked={stackSections} />;
    }

    if (isError || timedOut) {
      return (
        <BalanceSheetStateMessage
          message={t(
            'balanceSheetWidget.loadError',
            'Data could not be loaded. Please try again.',
          )}
          action={
            <Button
              variant='contained'
              onClick={handleRetry}
            >
              {t('retry', 'Retry')}
            </Button>
          }
        />
      );
    }

    if (isEmpty || hasNoCategories) {
      return (
        <BalanceSheetStateMessage
          message={t(
            'balanceSheetPage.noData',
            'No balance sheet data available.',
          )}
        />
      );
    }

    return (
      <BalanceSheetDetails
        balanceData={displayBalanceData}
        dateFilter={DataRangeFilterEnum.Custom}
        endDate={asOfDate}
        isLoading={isFetching}
        isWidget
        advisorView
        stacked={stackSections}
        disablePaperFrame
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        chartPercentModes={chartPercentModes}
        onChartPercentModesChange={setChartPercentModes}
      />
    );
  };

  return (
    <ContentContainer
      container={isWidget ? 'widget' : 'page'}
      ref={widgetWidthRef}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <WidgetHeader
          title={title ?? t('balanceSheet', 'Balance Sheet')}
          rightContent={
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <SingleDate
                label={t('date')}
                date={asOfDate}
                onChange={handleDateChange}
                useMobilePrimaryIconButton={narrowDateField}
                useWidgetNarrowIconField={narrowDateField}
              />
            </Box>
          }
        />
        {renderBody()}
      </Box>
      {isWidget && !isEmbeddableWidgetBundle && canOpenBalanceSheetPage && (
        <FooterButton
          buttonText={t(
            'balanceSheetWidget.viewFull',
            'View full balance sheet',
          )}
          onClick={goToBalanceSheetPage}
        />
      )}
    </ContentContainer>
  );
}
